// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/lib/apiClient.ts
//  Cliente HTTP centralizado. Substitui o Supabase client.
//  Injeta o JWT automaticamente em todas as requisições autenticadas.
// ════════════════════════════════════════════════════════════════════════════

import axios from 'axios';

// Em desenvolvimento, aponta para o Udisys.Api local.
// Em produção (endereço único), defina VITE_API_URL = https://api.udisysgestor.com.
// O tenant da empresa é resolvido pelo e-mail no login (não pelo endereço).
const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5000';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// ── Interceptor de Request — injeta o token JWT ───────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('udisys_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Interceptor de Response — trata erros globais ─────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status: number | undefined = error.response?.status;

    // 401 — token expirado/inválido: limpa sessão e redireciona (sem toast)
    // Ignora redirecionamento se o 401 veio dos próprios endpoints de autenticação
    const isAuthEndpoint = error.config?.url?.startsWith('/api/auth/');
    if (status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('udisys_token');
      localStorage.removeItem('udisys_user');
      window.location.href = '/auth';
      return Promise.reject(error);
    }

    // 402/403 de ASSINATURA (trial expirou/suspensa/cancelada no meio da sessão):
    // encerra a sessão e volta ao login, onde o bloqueio é explicado com a ação
    // certa (o gate do login devolve mensagem + link de pagamento).
    const codigoAssinatura = error.response?.data?.codigo;
    if (
      !isAuthEndpoint &&
      (status === 402 || status === 403) &&
      ['TRIAL_EXPIRADO', 'ASSINATURA_SUSPENSA', 'ASSINATURA_CANCELADA'].includes(codigoAssinatura)
    ) {
      localStorage.removeItem('udisys_token');
      localStorage.removeItem('udisys_user');
      window.location.href = '/auth';
      return Promise.reject(error);
    }

    // Reescreve error.message com a mensagem legível da API, para que
    // todos os onError handlers que usam error.message recebam o texto correto.
    const data = error.response?.data;
    if (data?.mensagem) {
      error.message = data.mensagem;
    } else if (Array.isArray(data?.erros) && data.erros.length > 0) {
      // Erros de validação FluentValidation: concatena todas as mensagens
      error.message = (data.erros as { mensagem: string }[])
        .map((e) => e.mensagem)
        .join(' — ');
    } else if (data?.errors && typeof data.errors === 'object') {
      // ProblemDetails do ASP.NET Core (model-binding/validação): { errors: { campo: [msg] } }
      const flat = Object.entries(data.errors as Record<string, string[]>)
        .flatMap(([campo, msgs]) => msgs.map((m) => `${campo}: ${m}`));
      if (flat.length > 0) error.message = flat.join(' — ');
    }

    return Promise.reject(error);
  }
);

// ── Helper para extrair mensagem de erro da API ───────────────────────────
export function getApiErrorMessage(error: unknown, fallback = 'Ocorreu um erro.'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data?.mensagem) return data.mensagem;
    if (data?.erros?.length) return data.erros[0].mensagem ?? fallback;
    if (data?.errors && typeof data.errors === 'object') {
      const flat = Object.entries(data.errors as Record<string, string[]>)
        .flatMap(([campo, msgs]) => msgs.map((m) => `${campo}: ${m}`));
      if (flat.length > 0) return flat.join(' — ');
    }
    if (data?.title) return data.title;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
